/**
 * What the live socket sends (see POST /api/v1/realtime/ticket). Envelopes carry ids only; the
 * screens refetch what changed through the normal, authorized endpoints.
 */
export interface RealtimeEnvelope {
  name: string;
  project_id: string | null;
  project_key: string | null;
  task_id: string | null;
  reference: string | null;
  version: number | null;
  actor_id: string | null;
  occurred_at: string;
}

export type ServerMessage =
  | { type: "ready" }
  | { type: "ping" }
  | { type: "resync" }
  | { type: "event"; event: RealtimeEnvelope };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function textOrNull(value: unknown): string | null {
  return typeof value === "string" && value !== "" ? value : null;
}

function envelopeOf(value: unknown): RealtimeEnvelope | null {
  if (!isRecord(value) || typeof value.name !== "string" || value.name === "") return null;
  return {
    name: value.name,
    project_id: textOrNull(value.project_id),
    project_key: textOrNull(value.project_key),
    task_id: textOrNull(value.task_id),
    reference: textOrNull(value.reference),
    version: typeof value.version === "number" ? value.version : null,
    actor_id: textOrNull(value.actor_id),
    occurred_at: textOrNull(value.occurred_at) ?? "",
  };
}

/** A socket frame as a message we understand, or null (ignored) for anything else. */
export function parseServerMessage(raw: unknown): ServerMessage | null {
  if (typeof raw !== "string") return null;
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(data)) return null;
  switch (data.type) {
    case "ready":
    case "ping":
    case "resync":
      return { type: data.type };
    case "event": {
      const event = envelopeOf(data.event);
      return event ? { type: "event", event } : null;
    }
    default:
      return null;
  }
}
